// frontend/src/app/focusGuard.ts
// ─────────────────────────────────────────────────────────────────────────────
// Fokus-Waechter gegen die Full-Keyboard-Access-Sackgasse in der WKWebView
// (Apple-Forum 806077): Ist auf dem Geraet "Full Keyboard Access" aktiv, kann
// der Fokus per Tab AUS der WebView heraustabben und findet danach nicht mehr
// zurueck — die App wirkt fuer Tastatur-Nutzer eingefroren.
//
// Strategie: Wir merken uns fortlaufend den zuletzt SINNVOLL fokussierten
// Knoten (nicht <body>, nicht null). Geht der Fokus verloren (er landet auf
// document.body oder null), setzen wir ihn auf den gemerkten Knoten zurueck —
// sofern dieser noch im DOM haengt.
//
// WICHTIG: Wir rufen NIEMALS element.blur() auf. Der Fokus wird IMMER auf ein
// Zielelement GESETZT. Grund (Apple-Forum 814432): Ctrl+Home / Ctrl+End nach
// einem programmatischen blur() kann ueber einen UIKit-Bug zum Absturz fuehren.
// Fokus verschieben = fokussieren, nie ent-fokussieren.

/**
 * Prueft, ob der Knoten ein sinnvolles Fokus-Ziel ist (im Dokument, ein
 * HTMLElement, und NICHT der <body>). document.body ist der Default-Fokus,
 * wenn "nichts" fokussiert ist — den wollen wir gerade NICHT merken.
 */
function isMeaningfulFocusTarget(node: EventTarget | null): node is HTMLElement {
  if (!(node instanceof HTMLElement)) return false;
  if (node === document.body) return false;
  if (typeof node.focus !== 'function') return false;
  return true;
}

/**
 * Installiert den Fokus-Waechter auf document/window. Liefert eine
 * Aufraeum-Funktion zurueck, die alle Listener wieder entfernt.
 */
export function installFocusGuard(): () => void {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return () => undefined;
  }

  // Der zuletzt sinnvoll fokussierte Knoten. Wird nur gesetzt, nie geleert —
  // ein verlorener Fokus soll ja genau hierhin zurueckkehren.
  let lastFocused: HTMLElement | null = null;
  // Re-Entrancy-Guard: unser eigenes focus() loest wieder focusin aus.
  let restoring = false;
  // E4a-Verify-Befund: Ein POINTER-initiierter Fokusverlust ist GEWOLLT —
  // Tipp neben ein Eingabefeld ist der Standardweg, die Bildschirmtastatur zu
  // schliessen, und WebKit-Buttons erhalten beim Tap keinen Fokus (focusout →
  // body). Der Waechter gilt der Full-Keyboard-Access-Sackgasse, nicht Touch/
  // Maus. Deshalb: Verluste kurz nach einem pointerdown NICHT wiederherstellen.
  let lastPointerTs = 0;
  const POINTER_GRACE_MS = 800;
  const onPointerDown = () => {
    lastPointerTs = Date.now();
  };

  // Bei jedem Fokuswechsel den (sinnvollen) Ziel-Knoten merken.
  const onFocusIn = (event: FocusEvent) => {
    if (isMeaningfulFocusTarget(event.target)) {
      lastFocused = event.target;
    }
  };

  // Zentrale Wiederherstellung: ist der aktuelle Fokus "verloren"
  // (body/null) und haben wir einen gemerkten Knoten, der noch im DOM
  // haengt → dorthin zurueckfokussieren (nie blur!).
  const restoreIfLost = () => {
    if (restoring) return;
    // Pointer-Gnadenfrist: der Nutzer hat bewusst daneben getippt/geklickt.
    if (Date.now() - lastPointerTs < POINTER_GRACE_MS) return;
    const active = document.activeElement;
    const lost = active === null || active === document.body;
    if (!lost) return;
    if (!lastFocused) return;
    // Knoten aus dem DOM entfernt/versteckt? Dann gibt es nichts zu retten.
    if (!lastFocused.isConnected) {
      lastFocused = null;
      return;
    }
    restoring = true;
    try {
      lastFocused.focus({ preventScroll: true });
    } finally {
      restoring = false;
    }
  };

  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('focusin', onFocusIn, true);
  // focusout meldet den Verlust unmittelbar; im naechsten Tick pruefen, ob
  // der Fokus tatsaechlich auf body/null gelandet ist (nicht auf ein neues
  // Ziel weitergewandert). window 'focus' faengt die Rueckkehr in die
  // WebView ab, nachdem der Fokus komplett heraus war.
  const onFocusOut = () => {
    window.setTimeout(restoreIfLost, 0);
  };
  document.addEventListener('focusout', onFocusOut, true);
  window.addEventListener('focus', restoreIfLost);

  return () => {
    document.removeEventListener('pointerdown', onPointerDown, true);
    document.removeEventListener('focusin', onFocusIn, true);
    document.removeEventListener('focusout', onFocusOut, true);
    window.removeEventListener('focus', restoreIfLost);
  };
}
